import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Plus, Trash2, Loader2, MessageSquare } from "lucide-react";
import { toast } from "sonner";

export default function AdminForum() {
  const [categories, setCategories] = useState([]);
  const [topics, setTopics] = useState([]);
  const [form, setForm] = useState({ name: "", description: "" });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const [c, t] = await Promise.all([api.get("/forum/categories"), api.get("/forum/topics")]);
      setCategories(c.data);
      setTopics(t.data);
    } catch {}
  };
  useEffect(() => { load(); }, []);

  const addCat = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/forum/categories", form);
      toast.success("Kategoria dodana");
      setForm({ name: "", description: "" });
      load();
    } catch { toast.error("Błąd"); }
    finally { setSaving(false); }
  };

  const delCat = async (id) => {
    if (!window.confirm("Usunąć kategorię wraz z wątkami?")) return;
    await api.delete(`/forum/categories/${id}`);
    toast.success("Usunięto");
    load();
  };

  const delTopic = async (id) => {
    if (!window.confirm("Usunąć wątek?")) return;
    await api.delete(`/forum/topics/${id}`);
    toast.success("Usunięto");
    load();
  };

  return (
    <div>
      <h2 className="font-display text-2xl font-bold text-ztk-navy dark:text-white">Moderacja forum</h2>
      <p className="mt-1 text-sm text-muted-foreground">Zarządzaj kategoriami i wątkami</p>

      <div className="mt-8 grid gap-8 lg:grid-cols-2">
        <div>
          <h3 className="mb-4 font-display text-lg font-semibold text-ztk-blue">Kategorie</h3>
          <form onSubmit={addCat} className="mb-5 space-y-3 rounded-xl border border-border bg-card p-4">
            <div><Label>Nazwa kategorii</Label><Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="forum-cat-name" className="mt-1.5" /></div>
            <div><Label>Opis</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="forum-cat-desc" className="mt-1.5" /></div>
            <Button type="submit" disabled={saving} className="gap-1.5 bg-ztk-blue" data-testid="forum-cat-add">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Dodaj kategorię</Button>
          </form>
          <div className="space-y-2">
            {categories.map((c) => (
              <div key={c.id} className="flex items-center justify-between rounded-lg border border-border bg-card p-3" data-testid={`admin-cat-${c.id}`}>
                <div>
                  <p className="font-medium text-ztk-navy dark:text-white">{c.name}</p>
                  <p className="text-xs text-muted-foreground">{c.topic_count} wątków</p>
                </div>
                <Button size="icon" variant="ghost" onClick={() => delCat(c.id)} className="text-destructive" data-testid={`cat-delete-${c.id}`}><Trash2 className="h-4 w-4" /></Button>
              </div>
            ))}
          </div>
        </div>

        <div>
          <h3 className="mb-4 font-display text-lg font-semibold text-ztk-blue">Wątki</h3>
          <div className="space-y-2">
            {topics.length === 0 && <p className="text-sm text-muted-foreground">Brak wątków</p>}
            {topics.map((t) => (
              <div key={t.id} className="flex items-center justify-between rounded-lg border border-border bg-card p-3" data-testid={`admin-topic-${t.id}`}>
                <div className="min-w-0">
                  <p className="truncate font-medium text-ztk-navy dark:text-white">{t.title}</p>
                  <p className="flex items-center gap-2 text-xs text-muted-foreground">{t.author_name} · <MessageSquare className="h-3 w-3" /> {t.reply_count}</p>
                </div>
                <Button size="icon" variant="ghost" onClick={() => delTopic(t.id)} className="text-destructive" data-testid={`topic-delete-${t.id}`}><Trash2 className="h-4 w-4" /></Button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
